import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function MolarityCalculator() {
  const [moles, setMoles] = useState("");
  const [volume, setVolume] = useState("");
  const [result, setResult] = useState<number | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Chemistry", url: "https://calclab.app/chemistry" },
      { name: "Molarity Calculator", url: "https://calclab.app/calculator/molarity" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Molarity Calculator",
      "Calculate molarity (moles per liter) for chemical solutions. Free online chemistry calculator with instant results.",
      "https://calclab.app/calculator/molarity",
      lang
    )
  ]);

  const calculateMolarity = () => {
    const m = parseFloat(moles);
    const v = parseFloat(volume);

    if (isNaN(m) || isNaN(v) || v === 0) {
      toast.error("Please enter valid values");
      return;
    }

    const molarity = m / v;
    setResult(molarity);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setMoles("");
    setVolume("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Molarity Calculator" 
      calculatorPath="/calculator/molarity"
      category="chemistry"
      subcategory="Solutions"
    >
      <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Molarity Calculator</h1>
            <p className="text-muted-foreground">
              Calculate the molar concentration of a solution using moles and volume
            </p>
          </div>

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Formula: M = n / V</CardTitle>
              <CardDescription>
                Where M is molarity (mol/L), n is moles (mol), and V is volume (L)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="moles">Moles (mol)</Label>
                  <Input
                    id="moles"
                    type="number"
                    step="0.001"
                    placeholder="Enter moles"
                    value={moles}
                    onChange={(e) => setMoles(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="volume">Volume (L)</Label>
                  <Input
                    id="volume"
                    type="number"
                    step="0.001"
                    placeholder="Enter volume in liters"
                    value={volume}
                    onChange={(e) => setVolume(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={calculateMolarity} className="flex-1">
                  <Calculator className="mr-2 h-4 w-4" />
                  Calculate
                </Button>
                <Button onClick={reset} variant="outline">
                  Reset
                </Button>
              </div>

              {result !== null && (
                <>
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Result</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-primary">
                        {result.toFixed(4)} M
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Molarity (mol/L)
                      </p>
                    </CardContent>
                  </Card>

                  <CalculatorSuperCalcCTA config={calculatorCTAs["molarity"]} />
                </>
              )}

              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">How to use</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <p>1. Enter the number of moles of solute</p>
                  <p>2. Enter the volume of solution in liters</p>
                  <p>3. Click Calculate to get the molarity</p>
                  <p className="pt-2 font-medium text-foreground">
                    Example: 2 moles in 1 liter = 2 M solution
                  </p>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </div>
    </PageLayout>
  );
}
